import { useAlarms } from '@ctcells/api'
import type { Alarm } from '@ctcells/contract'
import { dataSourceFor } from '@ctcells/core'
import { useEffect, useMemo, useSyncExternalStore } from 'react'

import { ALARM_PAGE_LIMIT, ALARM_SNAPSHOT_ARGS } from '../screens/alarms/alarmsQuery.ts'

/**
 * **알람 팝업** — 등급이 `CRITICAL` 인 알람이 새로 뜨면 셸이 창을 띄운다(eric 2026-10-30 「크리티컬만 띄우게」).
 * 그 밖의 등급(ERROR · WARN · INFO)은 띄우지 않는다.
 *
 * ## 판정은 알람 응답의 `severity` 다
 *
 * 🔴 **`Alarm.severity === 'CRITICAL'` 하나로 정한다.** 에러 코드 사전 표에서 등급을 추론하지 않는다 — 등급은 서버가 정한다(SSOT).
 *    (10-28 ~ 10-29 에는 «이 PC 에 저장한 팝업 코드 집합»으로 골랐다. 10-29 에 그 스위치를 걷어 켤 곳이 없어졌고, 10-30 에 등급 판정으로 바꾸며
 *    이 PC 저장소 `alarmPopupPrefs.ts` 는 지웠다.)
 *
 * ## 자료 — 헤더 배지와 «같은 질의»다
 *
 * 🔴 **왕복을 늘리지 않는다.** `useAlarms(ALARM_SNAPSHOT_ARGS)` 는 배지(`useAlarmBadge.ts`) · 알람 화면과 같은 캐시 키다.
 * 🔴 **주기 폴링이 아니다**(DP-01). 구독의 `alarm` 프레임이 `keys.alarms.all()` 을 무효화하고(`stream/bridge.ts`),
 *    새로 받은 목록에 «처음 보는 `alarm_id`»가 있으면 그것이 새 알람이다.
 *
 * ## 무엇을 띄우나
 *
 * - 🔴 **첫 목록은 «본 것»으로만 적는다** — 로그인 · 새로 고침마다 옛 알람(옛 CRITICAL 포함)이 한꺼번에 쏟아지지 않게.
 * - 처음 보는 `alarm_id` 중 `state === 'active'` 이고 `severity === 'CRITICAL'` 인 것만.
 * - **여러 건이 와도 창은 하나**다 — 열려 있는 창의 목록에 덧붙는다(창을 겹겹이 쌓지 않는다).
 * - 창에 든 알람이 다른 곳에서 확인 · 해제되면(활성이 아니게 되면) 그 줄은 창에서 빠진다 — 서버 상태를 따를 뿐이다.
 *
 * ## 🔴 팝업은 알림이다 — 알람을 바꾸지 않는다(DP-02)
 *
 * [닫기]는 창만 닫는다. 확인 · 해제는 알람 화면의 그 경로로만 한다. 자동 확인 · 자동 해제가 없다.
 *
 * ⚠ 장비 관점 콘솔 · 태블릿 셸은 이 훅을 부르지 않는다 — 통합 콘솔 셸(`ConsoleLayout`)만.
 */

/** 팝업을 띄우는 등급 — 서버가 준 `Alarm.severity` 와 그대로 견준다 */
export const POPUP_SEVERITY = 'CRITICAL' satisfies NonNullable<Alarm['severity']>

interface PopupState {
  /** 창에 든 알람 id — 들어온 차례 */
  queued: readonly string[]
}

let state: PopupState = { queued: [] }
/** 지금까지 본 `alarm_id` — `null` 이면 아직 기준선(첫 목록)을 안 잡았다 */
let seen: Set<string> | null = null
const listeners = new Set<() => void>()

function emit(next: PopupState) {
  state = next
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

const snapshot = () => state

/**
 * 처음 보는 알람 중 팝업 대상 — **순수 함수라 단독으로 시험한다.**
 * 활성(`state === 'active'`)이고 등급이 `CRITICAL` 인 것만.
 */
export function freshPopupIds(
  seenIds: ReadonlySet<string>,
  items: readonly Pick<Alarm, 'alarm_id' | 'state' | 'severity'>[],
): string[] {
  return items
    .filter(
      (alarm) =>
        !seenIds.has(alarm.alarm_id) &&
        alarm.state === 'active' &&
        alarm.severity === POPUP_SEVERITY,
    )
    .map((alarm) => alarm.alarm_id)
}

/** 새로 받은 목록 한 벌을 본다 — 첫 목록은 기준선만, 그 뒤는 처음 보는 팝업 대상을 창에 덧붙인다 */
export function observeAlarms(items: readonly Alarm[]) {
  if (seen === null) {
    seen = new Set(items.map((alarm) => alarm.alarm_id))
    return
  }
  const fresh = freshPopupIds(seen, items)
  for (const alarm of items) seen.add(alarm.alarm_id)
  if (fresh.length === 0) return
  const queued = [...state.queued, ...fresh.filter((id) => !state.queued.includes(id))]
  emit({ queued })
}

/** 창 닫기 — 🔴 알람은 그대로다(확인 · 해제가 아니다) */
export function dismissAlarmPopup() {
  if (state.queued.length > 0) emit({ queued: [] })
}

/** 시험 전용 — 모듈 보관소를 처음 상태로 */
export function resetAlarmPopupStore() {
  state = { queued: [] }
  seen = null
}

/** 셸이 쥐는 팝업 — 창에 띄울 알람(최신 값)과 닫기 */
export function useAlarmPopup(): { alarms: Alarm[]; dismiss: () => void } {
  const demo = dataSourceFor('alarms') === 'demo'
  /*
   * 🔴 훅은 조건 없이 부른다 — 끄는 것은 `skip` 이다. live 에서는 `ALARM_SNAPSHOT_ARGS` 그대로(캐시 키를 가르지 않는다).
   *    demo 에서는 띄우지 않는다 — 지어낸 알람으로 창을 띄우지 않는다.
   */
  const query = useAlarms(demo ? { limit: ALARM_PAGE_LIMIT, skip: true } : ALARM_SNAPSHOT_ARGS)
  const current = useSyncExternalStore(subscribe, snapshot)
  const items = query.isError ? undefined : query.data?.items

  useEffect(() => {
    if (items !== undefined) observeAlarms(items)
  }, [items])

  const alarms = useMemo(() => {
    if (items === undefined || current.queued.length === 0) return []
    const byId = new Map(items.map((alarm) => [alarm.alarm_id, alarm]))
    return current.queued
      .map((id) => byId.get(id))
      .filter((alarm): alarm is Alarm => alarm !== undefined && alarm.state === 'active')
  }, [items, current.queued])

  return { alarms, dismiss: dismissAlarmPopup }
}
